import { type Scene } from '@/designs-dev/scenes';
import { type Setup } from '@/james-playground/aa-family-claim/AaFamilyClaim';
import { ASK, OPENING_VOICE } from '@/james-playground/aa-family-claim/claim/asks';
import {
  hearVoice,
  readDocument,
  type ClaimState,
} from '@/james-playground/aa-family-claim/claim/claim';
import {
  nextSteps,
  openFor,
  openIn,
  statusOf,
  type Step,
} from '@/james-playground/aa-family-claim/claim/engine';
import { answerAll, confirm, skip } from '@/james-playground/aa-family-claim/claim/facts';
import { BATCH_GATES } from '@/james-playground/aa-family-claim/claim/asks';
import { entry } from '@/james-playground/aa-family-claim/claim/registry';

/**
 * Every named state of the claim, for the stage's select and the flows page.
 * Scenes are written once and run against whichever scenario the stage has
 * picked, so each pathway gets the same set of screens with its own people.
 */

type Prepare = (claim: ClaimState) => ClaimState;
const walk =
  (...steps: Prepare[]): Prepare =>
  (claim) =>
    steps.reduce((c, s) => s(c), claim);

const SIGNER: Record<ClaimState['scenario'], Record<string, string>> = {
  standard: { signingForSomeoneElse: 'No' },
  herself: { signingForSomeoneElse: 'No' },
  endOfLife: {
    signingForSomeoneElse: 'Yes',
    signerCategory:
      'I am claiming for them under the special rules for people nearing the end of life.',
  },
  careHome: { signingForSomeoneElse: 'No' },
  attorney: {
    signingForSomeoneElse: 'Yes',
    signerCategory: 'I hold Power of Attorney for Property and Finance',
    poaOnlineAccount: 'Yes',
  },
};

/** Before we start, answered the way each scenario's family would. */
export const before: Prepare = (claim) => {
  const ids = ['about-identity', 'about-address', 'start-award'].flatMap(
    (id) => ASK[id].entries,
  );
  let facts = ids.reduce(
    (f, id) => (f[id]?.state === 'proposed' ? confirm(f, id) : f),
    claim.facts,
  );
  // Entries with nothing to confirm (other names, title) are confirmed as none.
  facts = answerAll(
    facts,
    Object.fromEntries(ids.filter((id) => !facts[id]).map((id) => [id, ''])),
  );
  facts = answerAll(facts, {
    ...SIGNER[claim.scenario],
    specialRules: claim.scenario === 'endOfLife' ? 'Yes' : 'No',
    claimFormRequestedOn: '01/10/2026',
  });
  return { ...claim, facts };
};

const consented: Prepare = (claim) => ({ ...claim, aiConsent: 'yes' });
export const opening: Prepare = (claim) =>
  hearVoice(consented(claim), 'voice-opening').state;
const prescription: Prepare = (claim) =>
  readDocument(claim, 'prescription').state;
const seen: Prepare = (claim) => ({ ...claim, arrivals: [] });

/** Everything still open for "ready to send" set aside to talk through. */
const skipRest: Prepare = (claim) => {
  let facts = claim.facts;
  // A skipped gate leaves its follow-ups pending, so a few passes settle it.
  for (let pass = 0; pass < 4; pass += 1)
    for (const e of openFor(facts, 1)) facts = skip(facts, e.id);
  return { ...claim, facts, rounds: { ...claim.rounds, batchDone: true } };
};

const confirmed: Prepare = (claim) => ({
  ...claim,
  facts: Object.keys(claim.facts).reduce(
    (f, id) => (f[id].state === 'proposed' ? confirm(f, id) : f),
    claim.facts,
  ),
});

export const sent = walk(before, opening, seen, prescription, skipRest, confirmed);

const askStep =
  (id: string) =>
  (claim: ClaimState): Step => ({
    kind: 'ask',
    ask: ASK[id],
    score: 0,
    covers: openIn(ASK[id], claim.facts),
  });

/** The step the hub would offer first, for "the quickest next step". */
const firstStep = (claim: ClaimState): Step =>
  nextSteps(claim.facts, claim.rounds)[0] ?? askStep('about-ni')(claim);

export const AA_SECTIONS = [
  { id: 'start', label: 'Before we start' },
  { id: 'hub', label: 'The hub' },
  { id: 'rounds', label: 'Rounds' },
  { id: 'cards', label: 'Answer cards' },
  { id: 'send', label: 'Check and send' },
  { id: 'away', label: 'Leaving and coming back' },
  { id: 'handoffs', label: 'Other forms' },
];

const scene = (s: Scene<Setup>) => s;

const CARDS = [
  ['card-grid', 'Help in the day', 'day-washing'],
  ['card-night', 'Help at night', 'night-help'],
  ['card-watch', 'Keeping her safe', 'safe-watch'],
  ['card-falls', 'Falls', 'day-falls'],
  ['card-conditions', 'Conditions and medicines', 'health-conditions'],
  ['card-residence', 'Living in Great Britain', 'home-residence'],
  ['card-stay', 'Hospital and care stays', 'stays-now'],
  ['card-signer', 'Who signs', 'signing-signer'],
  ['card-consent', 'DWP contacting her doctors', 'signing-consent'],
  ['card-bank', 'Where the money goes', 'payment-bank'],
] as const;

const HANDOFFS = [
  ['handoff-scotland', 'Scotland', 'scotland'],
  ['handoff-ni', 'Northern Ireland', 'northernIreland'],
  ['handoff-pip', 'Under State Pension age', 'underSpa'],
  ['handoff-aa', 'Already gets AA', 'hasAa'],
  ['handoff-pip-now', 'Gets PIP or DLA', 'hasPip'],
  ['handoff-abroad', 'Lives abroad', 'abroad'],
] as const;

export const aaScenes: Scene<Setup>[] = [
  scene({ id: 'welcome', label: 'Welcome', kind: 'happy', section: 'start', initial: { frame: 'welcome' } }),
  scene({ id: 'before', label: 'What we have', kind: 'happy', section: 'start', initial: { frame: 'before' } }),
  scene({
    id: 'before-quiet',
    label: 'The quiet line',
    kind: 'happy',
    section: 'start',
    initial: {
      frame: 'before',
      prepare: (claim) => {
        const done = before(claim);
        const facts = { ...done.facts };
        delete facts.specialRules;
        delete facts.claimFormRequestedOn;
        return { ...done, facts };
      },
    },
  }),

  scene({ id: 'hub-new', label: 'Start with a voice note', kind: 'happy', section: 'hub', initial: { frame: 'hub', prepare: before } }),
  scene({ id: 'hub', label: 'After the voice note', kind: 'happy', section: 'hub', initial: { frame: 'hub', prepare: walk(before, opening) } }),
  scene({
    id: 'hub-photo',
    label: 'After the prescription',
    kind: 'happy',
    section: 'hub',
    initial: { frame: 'hub', prepare: walk(before, opening, seen, prescription) },
  }),
  scene({
    id: 'hub-ready',
    label: 'Ready to send',
    kind: 'happy',
    section: 'hub',
    initial: { frame: 'hub', prepare: walk(before, opening, seen, prescription, skipRest) },
  }),

  scene({
    id: 'round-opening',
    label: 'The opening voice note',
    kind: 'happy',
    section: 'rounds',
    initial: {
      frame: 'round',
      prepare: walk(before, consented),
      step: () => ({ kind: 'opening', prompt: OPENING_VOICE, score: 0, covers: [] }),
    },
  }),
  scene({
    id: 'round-recording',
    label: 'Recording',
    kind: 'happy',
    section: 'rounds',
    initial: {
      frame: 'round',
      prepare: walk(before, consented),
      step: () => ({ kind: 'opening', prompt: OPENING_VOICE, score: 0, covers: [] }),
      recording: { kind: 'recording', elapsed: 6000, hold: true },
    },
  }),
  scene({
    id: 'round-next',
    label: 'The quickest next step',
    kind: 'happy',
    section: 'rounds',
    initial: { frame: 'round', prepare: walk(before, opening), step: firstStep },
  }),
  scene({
    id: 'round-batch',
    label: 'The batch check',
    kind: 'happy',
    section: 'rounds',
    initial: {
      frame: 'round',
      prepare: walk(before, opening),
      step: (claim) => ({
        kind: 'batch',
        score: 0,
        covers: BATCH_GATES.map((g) => entry(g.id)).filter((e) => statusOf(e, claim.facts) === 'open'),
      }),
    },
  }),
  scene({
    id: 'round-prescription',
    label: 'A photo of the prescription',
    kind: 'happy',
    section: 'rounds',
    initial: {
      frame: 'round',
      prepare: walk(before, opening),
      step: () => ({ kind: 'document', doc: 'prescription', score: 0, covers: [] }),
    },
  }),

  ...CARDS.map(([id, label, askId]) =>
    scene({
      id,
      label,
      kind: 'happy',
      section: 'cards',
      initial: { frame: 'round', prepare: walk(before, opening), step: askStep(askId) },
    }),
  ),
  scene({
    id: 'card-words',
    label: 'The words on the form',
    kind: 'happy',
    section: 'cards',
    initial: {
      frame: 'round',
      prepare: walk(before, opening, seen, prescription, skipRest, confirmed),
      step: askStep('stronger-words'),
    },
  }),

  scene({ id: 'check', label: 'Check the answers', kind: 'happy', section: 'send', initial: { frame: 'check', prepare: walk(before, opening, seen, prescription, skipRest) } }),
  scene({ id: 'send', label: 'Send to your Care Adviser', kind: 'happy', section: 'send', initial: { frame: 'send', prepare: sent } }),
  scene({ id: 'sent', label: 'Sent', kind: 'happy', section: 'send', initial: { frame: 'sent', prepare: sent } }),
  scene({ id: 'home', label: 'Carry on later', kind: 'happy', section: 'away', initial: { frame: 'home', prepare: walk(before, opening) } }),

  ...HANDOFFS.map(([id, label, handoff]) =>
    scene({ id, label, kind: 'edge', section: 'handoffs', initial: { frame: 'handoff', handoff } }),
  ),
];

/** The scene a frame belongs to, so the select follows a click-through. */
export const sceneForFrame: Record<string, string> = {
  welcome: 'welcome',
  before: 'before',
  hub: 'hub',
  round: 'round-next',
  check: 'check',
  send: 'send',
  sent: 'sent',
  home: 'home',
  handoff: 'handoff-scotland',
};
